import React, { useState, useEffect,useRef } from "react";
import { Dialog, Box, Grid, Card, CardContent, Typography, Button, ButtonGroup } from '@mui/material';
import { LabelValueViewModel } from "../../Types/ExportTypes";
import { useLoading, useAuth, useSnackbar } from '../../Context/Contexts';
import { useTranslation } from "react-i18next";

import { Pie, Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    PointElement,
    LineElement,
    TimeScale,
    ChartOptions

} from "chart.js";

import "chartjs-adapter-date-fns";
ChartJS.register(
    CategoryScale,
    LinearScale,
    ArcElement,
    Title,
    ChartTooltip,
    Legend,
    PointElement,
    LineElement,
    TimeScale
);

const OrderCountGraph: React.FC = ({

}) => {

    const { authService, isAuthenticated } = useAuth();
    const { showSnackbar } = useSnackbar();
    const [orderGraphData, setOrderGraphData] = useState<LabelValueViewModel[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [orderGraphMode, setOrderGraphMode] = useState(2);
    const { t } = useTranslation();

    const initiatedRef = useRef(false);

    useEffect(() => {
        if (!initiatedRef.current) {
            initiatedRef.current = true;
            getOrderGraph();
        }
    }, []);

    const getOrderLineGraphData = (data?: LabelValueViewModel[]) => {
        const sorted = [...data ?? orderGraphData]
            .filter(x => x.label && x.value !== null)
            .sort((a, b) => new Date(a.label!).getTime() - new Date(b.label!).getTime());

        return {
            labels: sorted.map(x => x.label),
            datasets: [
                {
                    label: t("common.count"),
                    data: sorted.map(x => x.value),
                    borderColor: "#3f51b5",
                    backgroundColor: "rgba(63,81,181,0.2)",
                    fill: true,
                    tension: 0.3,
                },
            ],
        };
    };

    const [orderLineGraph, setOrderLineGraph] = useState(getOrderLineGraphData);

    const lineOptions: ChartOptions<"line"> = {
        responsive: true,
        plugins: {
            legend: { position: "bottom" },
            title: { display: true, text: t("common.orderCountOverTime") },
        },
        scales: {
            x: {
                type: "time",
                time: {
                    unit: "day",
                    displayFormats: {
                        day: "yyyy-MM-dd",
                    },
                    tooltipFormat: "yyyy-MM-dd",
                },

                title: {
                    display: true,
                    text: t("common.date"),
                },
            },
            y: {
                title: {
                    display: true,
                    text: t("common.count"),
                },
            },
        },
    };


    const getOrderGraph = async (graphModeOverride?: number | null) => {
        var mode = graphModeOverride ?? orderGraphMode;
        setLoading(true);
        try {
            const response = await authService.get<{ items: LabelValueViewModel[] }>(
                `/graphApi/GetOrderGraph?mode=${mode}`
            );
            setOrderGraphData(prev => {
                const lineData = getOrderLineGraphData(response.items);
                setOrderLineGraph(lineData);
                return response.items;
            });
        } catch (err) {
            console.error("getOrderGraph failed", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>

                <Card>
                    <Box sx={{ backgroundColor: "#f5f7fa", borderRadius: 2, p: 3 }}>
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={{ display: "flex", justifyContent: "center" }}
                        >
                            {t("graph.orderGraphTitle")}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ color: "#333", mb: 2, display: "flex", justifyContent: "center" }}
                        >
                            {t("graph.orderGraphDescription")}
                        </Typography>

                        <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                            <ButtonGroup variant="outlined">
                                <Button
                                    onClick={() => {
                                        setOrderGraphMode(1);
                                        getOrderGraph(1);
                                    }}
                                    variant={orderGraphMode === 1 ? "contained" : "outlined"}
                                >
                                    {t("common.week")}
                                </Button>
                                <Button
                                    onClick={() => {
                                        setOrderGraphMode(2);
                                        getOrderGraph(2);
                                    }}
                                    variant={orderGraphMode === 2 ? "contained" : "outlined"}
                                >
                                    {t("common.month")}
                                </Button>
                            </ButtonGroup>
                        </Box>

                        <Box sx={{ height: 400, display: "flex", justifyContent: "center" }}>
                        {loading ? (
                            <Typography>Loading...</Typography>
                        ) : (
                            orderLineGraph && <Line data={orderLineGraph} options={lineOptions} />
                        )}
                        </Box>

                        <Typography
                            variant="caption"
                            sx={{ color: "#888", mt: 2, display: "flex", justifyContent: "center" }}
                        >
                            {t("graph.orderExtraInfo")}
                        </Typography>
                    </Box>
                </Card>

        </>
    );

};

export default OrderCountGraph;
